import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  arrayMove,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import { GripVertical } from 'lucide-react';

import {
  Button,
  Card,
  Badge,
  Input,
  Label,
  Switch,
  Alert,
  Dialog,
  DialogHeader,
  DialogTitle,
  DialogContent,
  DialogFooter,
  EmptyState,
  PageHeader,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tabs,
  TabsList,
  TabsTrigger,
} from '@/components/ui';
import { ComercialIcon } from '@/components/icons';
import {
  useCatalogItems,
  useCatalogActions,
  bootstrapCatalogs,
  type CatalogItem,
} from '@/lib/catalogs-store';
import { useDeals } from '@/lib/comercial-store';
import { useMyFeatureFlag } from '@/lib/feature-flags-store';
import { useAuth } from '@/contexts/AuthContext';
// Parte 3 / F1: os catálogos de Projetos (P3/P4/P7) passam a viver aqui, dentro
// de Catálogos (decisão do Matheus) — em vez de atalhos no topo da página Projetos.
import { ProjetoDocumentoTiposPage } from '@/pages/admin/projetos/ProjetoDocumentoTiposPage';
// ROTAS-ORFAS (06/08, decisao 7 do Matheus): "Tipos de Projeto" era rota sem
// casa na navegacao (/admin/projeto-tipos, 2 tipos cadastrados). E o mesmo
// assunto da aba Projetos, em dois lugares — passa a viver aqui dentro.
import { ProjetoTiposPage } from '@/pages/admin/projetos/ProjetoTiposPage';
// Faxina2 / FB1: os catálogos "avançados" (ex-"Catálogos R2" — nome técnico que
// vazava) viram uma área dentro de Catálogos. Uma tela só, sem "R2" no menu.
import { CatalogsR2Page } from '@/pages/admin/CatalogsR2Page';
// Ferramenta de Orçamento O1: configuração (markups / MO / pagamento / taxas).
import { OrcamentoConfigPage } from '@/pages/admin/orcamento/OrcamentoConfigPage';
// Compras Round D / D1: tipos de item + fornecedores + alíquotas NF.
import { ComprasConfigPage } from '@/pages/admin/compras/ComprasConfigPage';

type Dept = 'comercial' | 'projetos' | 'avancados' | 'orcamento' | 'compras';
type CatalogList = 'produtos' | 'servicos' | 'origens' | 'documentos-exigidos';

const COMERCIAL_LISTS: Array<{ slug: CatalogList; label: string }> = [
  { slug: 'produtos', label: 'Produtos' },
  { slug: 'servicos', label: 'Serviços' },
  { slug: 'origens', label: 'Origens' },
  // PROJETOS-LIMPO 2 (14/09/2026): "Documentos exigidos" é regra do GANHO (o Comercial
  // anexa, a venda não vira projeto sem eles) — mudou de Projetos para cá.
  { slug: 'documentos-exigidos', label: 'Documentos exigidos' },
];

const PROJETO_CATALOGS = [
  // PROJETOS-LIMPO 2 (14/09/2026): "Atividades padrão" MORREU (decisão do Matheus: seis tarefas
  // intocadas desde 01/07; o Cronograma faz isso melhor).
  { slug: 'tipos', label: 'Tipos de projeto' },
  // SU-3: "Situação do material" (P7) removido — status unificado no Compras.
  // PROJETOS-LIMPO 2: "Documentos exigidos" foi para Comercial (é regra do Ganho).
] as const;
type ProjetoCatalog = (typeof PROJETO_CATALOGS)[number]['slug'];

const DAY = 1000 * 60 * 60 * 24;
const STALE_THRESHOLD_DAYS = 30;

/**
 * /admin/catalogos — gestão dos catálogos controlados (regra 10).
 *
 * Layout:
 *   - Toggle de departamento no topo (single-option agora)
 *   - Sidebar com listas (produtos · serviços · origens)
 *   - Painel central: tabela de itens com label · ordem · ativo · uso · ações
 *   - Drag pra reordenar via @dnd-kit/sortable
 *   - Delete BLOQUEADO se uso > 0 — modal oferece "desativar em vez de excluir"
 *
 * Uso = contagem de cards (deals) referenciando o item ID. Sigla cada item
 * com data-testid pra tests + reaproveitamento futuro.
 */
export function CatalogsPage() {
  const projetosV2 = useMyFeatureFlag('PROJETOS_V2_ENABLED');
  const orcamentoOn = useMyFeatureFlag('ORCAMENTO_ENABLED');
  const comprasOn = useMyFeatureFlag('COMPRAS_ENABLED');
  const { user } = useAuth();
  // FB1: a área "Avançados" (ex-"Catálogos R2") mantém o RBAC da tela antiga —
  // só administrativo/super-admin (= TELA_SLUGS_VISIVEIS['catalogs-r2']). A rota
  // legada /admin/catalogos é aberta à área admin, então gateamos AQUI dentro.
  const podeVerAvancados =
    !!user?.isSuperAdmin || user?.profile?.slug === 'administrativo';
  // D-3 (#57): `?dept=` escolhe a aba inicial — é o que permite o item
  // "Impostos" do Financeiro apontar direto pra aba Compras (onde a alíquota
  // é editada). Valor inválido cai no default; nada persiste no client.
  const [searchParams] = useSearchParams();
  const deptDaUrl = searchParams.get('dept');
  const [dept, setDept] = useState<Dept>(
    deptDaUrl === 'projetos' || deptDaUrl === 'orcamento' || deptDaUrl === 'compras' || deptDaUrl === 'avancados'
      ? (deptDaUrl as Dept)
      : 'comercial',
  );
  const [list, setList] = useState<CatalogList>(COMERCIAL_LISTS[0].slug);
  // Parte 3 / F1: catálogo de Projetos selecionado na sub-lista.
  const [projCat, setProjCat] = useState<ProjetoCatalog>('tipos');

  // Garantir bootstrap idempotente — primeira visita à página popula os catálogos
  useEffect(() => {
    bootstrapCatalogs();
  }, []);

  // "Projetos" só aparece como departamento com o módulo Projetos 2.0 ligado.
  // "Avançados" (FB1) reúne os catálogos administráveis (ex-"Catálogos R2" —
  // nome técnico que vazava). Mesma tela, sem "R2" no menu.
  const depts: Array<{ slug: Dept; label: string }> = [
    { slug: 'comercial', label: 'Comercial' },
    ...(projetosV2 ? [{ slug: 'projetos' as const, label: 'Projetos' }] : []),
    // O1: a configuração da Ferramenta de Orçamento entra como uma área aqui,
    // gateada pela flag ORCAMENTO_ENABLED (default OFF).
    ...(orcamentoOn ? [{ slug: 'orcamento' as const, label: 'Orçamento' }] : []),
    // Round D: o módulo de Compras (tipos de item · fornecedores · alíquotas NF).
    ...(comprasOn ? [{ slug: 'compras' as const, label: 'Compras' }] : []),
    ...(podeVerAvancados ? [{ slug: 'avancados' as const, label: 'Avançados' }] : []),
  ];

  return (
    <section>
      <PageHeader
        crumb="Configurações · Cadastros"
        title="Central de catálogos"
        subtitle="As listas prontas que aparecem nos campos de escolha do sistema — produtos, serviços, origens e os catálogos de cada área."
      />

      {/* FASE-C: a fileira de "Área:" virou o Tabs canônico — 1 linha rolável
          no 375px (antes eram pílulas soltas que quebravam em 2 linhas).
          Mesmos itens, mesma ordem, mesmos rótulos e data-testid. */}
      {depts.length > 1 && (
        <Tabs value={dept} onValueChange={(v) => setDept(v as Dept)} className="mb-6">
          <TabsList>
            {depts.map((d) => (
              <TabsTrigger key={d.slug} value={d.slug} data-testid={`catalog-dept-${d.slug}`}>
                {d.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
      )}

      {dept === 'comercial' && (
        <Alert variant="info" rail className="mb-4">
          <strong>Por que usar catálogos?</strong> Escolher de uma lista pronta evita
          o mesmo item escrito de dez jeitos (&quot;CFTV&quot; vs &quot;C.F.T.V.&quot;),
          o que estragaria filtros e relatórios. Item desativado some das novas
          escolhas, mas continua aparecendo onde já estava.
        </Alert>
      )}

      {dept === 'comercial' && (
        <div className="grid grid-cols-1 lg:grid-cols-[200px_1fr] gap-4">
          {/* Sidebar de listas */}
          <Card variant="flat" className="min-w-0 p-2">
            <nav className="flex flex-col gap-1">
              {COMERCIAL_LISTS.map((l) => (
                <button
                  key={l.slug}
                  type="button"
                  onClick={() => setList(l.slug)}
                  className={`text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                    list === l.slug
                      ? 'bg-red-soft dark:bg-red-soft-dark text-red-deep dark:text-red-glow font-medium'
                      : 'text-ink-2 dark:text-ink-dark-2 hover:bg-paper-warm dark:hover:bg-paper-dark-warm'
                  }`}
                  data-testid={`catalog-list-${l.slug}`}
                >
                  {l.label}
                </button>
              ))}
            </nav>
          </Card>

          {/* Painel principal */}
          <div className="min-w-0" data-testid={`catalog-comercial-panel-${list}`}>
            {list === 'documentos-exigidos' ? <ProjetoDocumentoTiposPage /> : <CatalogTable dept="comercial" list={list} />}
          </div>
        </div>
      )}

      {dept === 'projetos' && (
        <div className="grid grid-cols-1 lg:grid-cols-[200px_1fr] gap-4">
          {/* Sidebar dos catálogos de Projetos */}
          <Card variant="flat" className="min-w-0 p-2">
            <nav className="flex flex-col gap-1">
              {PROJETO_CATALOGS.map((c) => (
                <button
                  key={c.slug}
                  type="button"
                  onClick={() => setProjCat(c.slug)}
                  className={`text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                    projCat === c.slug
                      ? 'bg-red-soft dark:bg-red-soft-dark text-red-deep dark:text-red-glow font-medium'
                      : 'text-ink-2 dark:text-ink-dark-2 hover:bg-paper-warm dark:hover:bg-paper-dark-warm'
                  }`}
                  data-testid={`catalog-projeto-${c.slug}`}
                >
                  {c.label}
                </button>
              ))}
            </nav>
          </Card>

          {/* Painel principal — o catálogo de Projetos escolhido (embutido) */}
          <div className="min-w-0" data-testid={`catalog-projeto-panel-${projCat}`}>
            {projCat === 'tipos' && <ProjetoTiposPage />}
          </div>
        </div>
      )}

      {/* O1: configuração da Ferramenta de Orçamento embutida (markups, mão de
          obra, condições de pagamento, taxas dos simuladores). Gate redundante
          pela flag (a aba já é gateada) por defesa em profundidade. */}
      {dept === 'orcamento' && orcamentoOn && (
        <div className="min-w-0" data-testid="catalog-orcamento-panel">
          <OrcamentoConfigPage embedded />
        </div>
      )}

      {/* Round D: módulo de Compras embutido (tipos de item, fornecedores,
          alíquotas NF). Gate redundante pela flag (a aba já é gateada). */}
      {dept === 'compras' && comprasOn && (
        <div className="min-w-0" data-testid="catalog-compras-panel">
          <ComprasConfigPage embedded />
        </div>
      )}

      {/* FB1: catálogos "Avançados" (ex-"Catálogos R2") embutidos — uma tela só.
          Gate redundante (a aba já é gateada) por defesa em profundidade. */}
      {dept === 'avancados' && podeVerAvancados && (
        <div className="min-w-0" data-testid="catalog-avancados-panel">
          <CatalogsR2Page />
        </div>
      )}
    </section>
  );
}

// ─── Tabela de itens ─────────────────────────────────────────────────────

interface CatalogTableProps {
  dept: Dept;
  list: CatalogList;
}

/**
 * Exportada em 09/08/2026 para a migracao dos Catalogos: cada lista do
 * Comercial virou um item proprio da Configuracoes, e todos renderizam ESTA
 * tabela com a lista escolhida. Uma implementacao so — copiar a tabela por item
 * seria tres lugares para consertar o mesmo bug de arrasto ou de contagem de
 * uso.
 */
export function CatalogTable({ dept, list }: CatalogTableProps) {
  const items = useCatalogItems(dept, list);
  const allDeals = useDeals();
  const { createItem, updateItem, reorderItems, toggleActive, deleteItem } = useCatalogActions(
    dept,
    list
  );

  const [creating, setCreating] = useState(false);
  const [newLabel, setNewLabel] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingLabel, setEditingLabel] = useState('');
  const [confirmDelete, setConfirmDelete] = useState<CatalogItem | null>(null);

  // Contagem de uso por item (cards referenciando o ID)
  const usage = useMemo(() => {
    const map = new Map<string, number>();
    for (const d of allDeals) {
      let ref: string | undefined;
      if (list === 'produtos') ref = d.produto;
      else if (list === 'servicos') ref = d.servico;
      else if (list === 'origens') ref = d.origem;
      if (ref) map.set(ref, (map.get(ref) ?? 0) + 1);
    }
    return map;
  }, [allDeals, list]);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = items.findIndex((i) => i.id === active.id);
    const newIndex = items.findIndex((i) => i.id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    const reordered = arrayMove(items, oldIndex, newIndex);
    reorderItems(reordered.map((i) => i.id));
  }

  function handleCreate() {
    const label = newLabel.trim();
    if (label.length < 2) return;
    createItem({ label });
    setNewLabel('');
    setCreating(false);
  }

  function handleEditCommit(id: string) {
    const trimmed = editingLabel.trim();
    if (trimmed.length >= 2) {
      updateItem(id, { label: trimmed });
    }
    setEditingId(null);
    setEditingLabel('');
  }

  function handleDeleteAttempt(item: CatalogItem) {
    setConfirmDelete(item);
  }

  function handleConfirmDelete() {
    if (!confirmDelete) return;
    deleteItem(confirmDelete.id);
    setConfirmDelete(null);
  }

  function handleDeactivateInstead() {
    if (!confirmDelete) return;
    updateItem(confirmDelete.id, { ativo: false });
    setConfirmDelete(null);
  }

  const itemConfirmUsage = confirmDelete ? usage.get(confirmDelete.id) ?? 0 : 0;

  if (items.length === 0) {
    return (
      <Card variant="flat">
        <EmptyState
          icon={<ComercialIcon className="w-6 h-6" />}
          title="Nenhum item neste catálogo"
          description="Adicione o primeiro item pra começar a usar."
          action={<Button onClick={() => setCreating(true)}>+ Novo item</Button>}
        />
        <CreateItemDialog
          open={creating}
          newLabel={newLabel}
          setNewLabel={setNewLabel}
          onConfirm={handleCreate}
          onCancel={() => {
            setCreating(false);
            setNewLabel('');
          }}
        />
      </Card>
    );
  }

  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <p className="min-w-0 text-sm text-ink-2 dark:text-ink-dark-2">
          {items.length} {items.length === 1 ? 'item' : 'itens'} ·{' '}
          {items.filter((i) => i.ativo).length} ativos
        </p>
        <Button size="sm" onClick={() => setCreating(true)}>
          + Novo item
        </Button>
      </div>

      {/* FASE-C: a grade fixa `40px 1fr 120px 100px 180px` (≈440px + gaps)
          estourava o 375px. Agora é o <Table> canônico, que já embrulha em
          `overflow-x-auto` — a linha continua arrastável (dnd-kit no <tr>). */}
      <Card variant="flat">
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-10" aria-label="Ordem" />
                <TableHead>Item</TableHead>
                <TableHead className="text-center">Em uso</TableHead>
                <TableHead className="text-center">Ativo</TableHead>
                <TableHead className="text-right">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <SortableContext items={items.map((i) => i.id)} strategy={verticalListSortingStrategy}>
              <TableBody>
                {items.map((item) => {
                  const uso = usage.get(item.id) ?? 0;
                  const isStale = item.ativo && uso === 0 && isOlderThanDays(item.criadoEmIso, STALE_THRESHOLD_DAYS);
                  return (
                    <CatalogRow
                      key={item.id}
                      item={item}
                      uso={uso}
                      isStale={isStale}
                      isEditing={editingId === item.id}
                      editingLabel={editingLabel}
                      onStartEdit={() => {
                        setEditingId(item.id);
                        setEditingLabel(item.label);
                      }}
                      onChangeEditing={setEditingLabel}
                      onCommitEdit={() => handleEditCommit(item.id)}
                      onCancelEdit={() => {
                        setEditingId(null);
                        setEditingLabel('');
                      }}
                      onToggleActive={() => toggleActive(item.id)}
                      onDelete={() => handleDeleteAttempt(item)}
                    />
                  );
                })}
              </TableBody>
            </SortableContext>
          </Table>
        </DndContext>
      </Card>

      <CreateItemDialog
        open={creating}
        newLabel={newLabel}
        setNewLabel={setNewLabel}
        onConfirm={handleCreate}
        onCancel={() => {
          setCreating(false);
          setNewLabel('');
        }}
      />

      <Dialog
        open={!!confirmDelete}
        onOpenChange={(open) => !open && setConfirmDelete(null)}
        size="sm"
      >
        {confirmDelete && (
          <>
            <DialogHeader>
              <DialogTitle>
                {itemConfirmUsage > 0 ? 'Não dá pra excluir' : 'Excluir item?'}
              </DialogTitle>
            </DialogHeader>
            <DialogContent>
              {itemConfirmUsage > 0 ? (
                <p className="text-sm text-ink-2 dark:text-ink-dark-2">
                  <strong>{itemConfirmUsage}</strong>{' '}
                  {itemConfirmUsage === 1 ? 'card usa este item' : 'cards usam este item'}.
                  Pra preservar o histórico, desative em vez de excluir — itens inativos não
                  aparecem em novos cards mas continuam visíveis nos existentes.
                </p>
              ) : (
                <p className="text-sm text-ink-2 dark:text-ink-dark-2">
                  Excluir <strong>{confirmDelete.label}</strong>? Nenhum card usa esse item,
                  então a operação é segura — mas é irreversível.
                </p>
              )}
            </DialogContent>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setConfirmDelete(null)}>
                Cancelar
              </Button>
              {itemConfirmUsage > 0 ? (
                <Button onClick={handleDeactivateInstead}>Desativar em vez de excluir</Button>
              ) : (
                <Button variant="destructive" onClick={handleConfirmDelete}>
                  Excluir
                </Button>
              )}
            </DialogFooter>
          </>
        )}
      </Dialog>
    </>
  );
}

// ─── Linha sortable ──────────────────────────────────────────────────────

interface CatalogRowProps {
  item: CatalogItem;
  uso: number;
  isStale: boolean;
  isEditing: boolean;
  editingLabel: string;
  onStartEdit: () => void;
  onChangeEditing: (s: string) => void;
  onCommitEdit: () => void;
  onCancelEdit: () => void;
  onToggleActive: () => void;
  onDelete: () => void;
}

function CatalogRow({
  item,
  uso,
  isStale,
  isEditing,
  editingLabel,
  onStartEdit,
  onChangeEditing,
  onCommitEdit,
  onCancelEdit,
  onToggleActive,
  onDelete,
}: CatalogRowProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: item.id,
  });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };

  return (
    <TableRow ref={setNodeRef} style={style} data-testid={`catalog-row-${item.id}`}>
      {/* Alça de arrastar */}
      <TableCell className="w-10">
        <button
          type="button"
          {...attributes}
          {...listeners}
          className="cursor-grab active:cursor-grabbing text-ink-3 hover:text-ink-1 dark:hover:text-ink-dark-1"
          aria-label={`Reordenar ${item.label}`}
        >
          <GripVertical className="h-4 w-4" aria-hidden="true" />
        </button>
      </TableCell>

      {/* Nome (editável direto na linha) */}
      <TableCell className="min-w-0">
        {isEditing ? (
          <Input
            size="sm"
            value={editingLabel}
            autoFocus
            onChange={(e) => onChangeEditing(e.target.value)}
            onBlur={onCommitEdit}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                onCommitEdit();
              } else if (e.key === 'Escape') {
                e.preventDefault();
                onCancelEdit();
              }
            }}
          />
        ) : (
          <button
            type="button"
            onClick={onStartEdit}
            className="flex w-full min-w-0 flex-wrap items-center gap-2 text-left text-sm text-ink-1 dark:text-ink-dark-1 hover:text-red transition-colors"
            title="Clique para renomear"
          >
            <span className="min-w-0 break-words">{item.label}</span>
            {!item.ativo && (
              <Badge variant="neutral" size="sm">
                Inativo
              </Badge>
            )}
            {isStale && (
              <Badge variant="warning" size="sm" title="Sem uso há mais de 30 dias">
                Sem uso
              </Badge>
            )}
          </button>
        )}
      </TableCell>

      {/* Em uso */}
      <TableCell className="text-center font-mono text-sm text-ink-2 dark:text-ink-dark-2">
        {uso === 0 ? <span className="text-ink-3 dark:text-ink-dark-3">—</span> : uso}
      </TableCell>

      {/* Ativo */}
      <TableCell>
        <div className="flex justify-center">
          <Switch
            checked={item.ativo}
            onChange={onToggleActive}
            aria-label={`Ligar/desligar ${item.label}`}
          />
        </div>
      </TableCell>

      {/* Ações */}
      <TableCell className="text-right">
        <Button
          size="sm"
          variant="ghost"
          onClick={onDelete}
          className="text-red hover:bg-red-soft dark:hover:bg-red-soft-dark"
        >
          Excluir
        </Button>
      </TableCell>
    </TableRow>
  );
}

// ─── Dialog criar item ───────────────────────────────────────────────────

interface CreateItemDialogProps {
  open: boolean;
  newLabel: string;
  setNewLabel: (s: string) => void;
  onConfirm: () => void;
  onCancel: () => void;
}

function CreateItemDialog({ open, newLabel, setNewLabel, onConfirm, onCancel }: CreateItemDialogProps) {
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onCancel()} size="sm">
      <DialogHeader>
        <DialogTitle>Novo item de catálogo</DialogTitle>
      </DialogHeader>
      <DialogContent>
        <Label htmlFor="new-item-label">Nome do item</Label>
        <Input
          id="new-item-label"
          autoFocus
          value={newLabel}
          onChange={(e) => setNewLabel(e.target.value)}
          placeholder="Ex: Câmera IP Hikvision DS-2CD"
          onKeyDown={(e) => {
            if (e.key === 'Enter' && newLabel.trim().length >= 2) {
              e.preventDefault();
              onConfirm();
            }
          }}
        />
      </DialogContent>
      <DialogFooter>
        <Button variant="ghost" onClick={onCancel}>
          Cancelar
        </Button>
        <Button onClick={onConfirm} disabled={newLabel.trim().length < 2}>
          Adicionar
        </Button>
      </DialogFooter>
    </Dialog>
  );
}

// ─── Util ────────────────────────────────────────────────────────────────

function isOlderThanDays(iso: string, days: number): boolean {
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return false;
  return Date.now() - t > days * DAY;
}
